import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { gigSwal } from '../utils/swal';
import { MapPin, Calendar, Music, FileText, Printer, Navigation, Lock, Zap } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import useIsMobile from '../hooks/useIsMobile';

const API_URL = process.env.REACT_APP_API_URL || '';

const emptyForm = {
  gig_name: '', venue_name: '', venue_location: '', venue_postcode: '',
  gig_date: '', gig_time: '', number_of_sets: 1, set_duration_minutes: '',
  notes: '', stage_info: '', power_info: '',
};

function GigManagement() {
  const theme = useTheme();
  const isNarrow = useIsMobile(600);
  const [gigs, setGigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedGigId, setExpandedGigId] = useState(null);
  const [showPastGigs, setShowPastGigs] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [wizardMode, setWizardMode] = useState('add');
  const [editingGigId, setEditingGigId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [autoSettings, setAutoSettings] = useState({
    auto_activate_enabled: false,
    auto_activate_mins_before: 120,
    auto_deactivate_mins_after: 120,
  });

  useEffect(() => {
    fetchGigs();
    axios.get(`${API_URL}/api/settings`)
      .then(res => {
        setAutoSettings({
          auto_activate_enabled: !!res.data.auto_activate_enabled,
          auto_activate_mins_before: res.data.auto_activate_mins_before ?? 120,
          auto_deactivate_mins_after: res.data.auto_deactivate_mins_after ?? 120,
        });
      })
      .catch(() => {});
  }, []);

  const fetchGigs = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/gigs`);
      setGigs(res.data);
      setLoading(false);
      const editId = new URLSearchParams(window.location.search).get('edit');
      if (editId) {
        const gig = res.data.find(g => g.id === parseInt(editId));
        if (gig) openEditWizard(gig);
      }
    } catch {
      setLoading(false);
    }
  };

  const openAddWizard = () => {
    setFormData(emptyForm);
    setWizardMode('add');
    setEditingGigId(null);
    setWizardStep(1);
    setWizardOpen(true);
  };

  const openEditWizard = (gig) => {
    setFormData({
      gig_name: gig.gig_name,
      venue_name: gig.venue_name,
      venue_location: gig.venue_location || '',
      venue_postcode: gig.venue_postcode || '',
      gig_date: gig.gig_date ? String(gig.gig_date).split('T')[0] : '',
      gig_time: gig.gig_time ? String(gig.gig_time).substring(0, 5) : '',
      number_of_sets: gig.number_of_sets || 1,
      set_duration_minutes: gig.set_duration_minutes || '',
      notes: gig.notes || '',
      stage_info: gig.stage_info || '',
      power_info: gig.power_info || '',
    });
    setWizardMode('edit');
    setEditingGigId(gig.id);
    setWizardStep(1);
    setWizardOpen(true);
  };

  const closeWizard = () => {
    setWizardOpen(false);
    setEditingGigId(null);
  };

  const handleSubmit = async () => {
    if (!formData.gig_name.trim()) { toast.error('Gig name is required'); setWizardStep(1); return; }
    if (!formData.venue_name.trim()) { toast.error('Venue name is required'); setWizardStep(2); return; }
    setSubmitting(true);
    try {
      if (wizardMode === 'add') {
        await axios.post(`${API_URL}/api/gigs`, formData);
        toast.success('Gig created');
      } else {
        await axios.put(`${API_URL}/api/gigs/${editingGigId}`, formData);
        toast.success('Gig updated');
      }
      closeWizard();
      fetchGigs();
    } catch {
      toast.error(wizardMode === 'add' ? 'Failed to create gig' : 'Failed to update gig');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (gigId, gigName) => {
    const result = await gigSwal(theme, {
      title: 'Delete Gig?',
      text: `Are you sure you want to delete "${gigName}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      confirmButtonText: 'Yes, delete it',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
    });
    if (!result.isConfirmed) return;
    try {
      await axios.delete(`${API_URL}/api/gigs/${gigId}`);
      fetchGigs();
      toast.success('Gig deleted');
    } catch (err) {
      toast.error(err.response?.data?.hasRequests ? 'Cannot delete gig with request history' : 'Failed to delete gig');
    }
  };

  const handleActivate = async (gigId, gigName) => {
    try {
      await axios.patch(`${API_URL}/api/gigs/${gigId}/activate`);
      fetchGigs();
      toast.success(`Active gig: ${gigName}`);
    } catch {
      toast.error('Failed to activate gig');
    }
  };

  const openDirections = (e, gig) => {
    e.stopPropagation();
    const query = encodeURIComponent([gig.venue_name, gig.venue_postcode].filter(Boolean).join(', '));
    const isApple = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const url = isApple
      ? `https://maps.apple.com/?q=${query}`
      : `https://www.google.com/maps/search/?api=1&query=${query}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const isPastGig = (gig) => {
    if (!gig.gig_date) return false;
    const d = new Date(gig.gig_date);
    d.setDate(d.getDate() + 1);
    d.setHours(23, 59, 59);
    return d < new Date();
  };

  const getAutoActivateInfo = (gig) => {
    if (!autoSettings.auto_activate_enabled || !gig.gig_date || !gig.gig_time) return null;
    const gigDate = String(gig.gig_date).split('T')[0];
    const timeStr = String(gig.gig_time).substring(0, 5);
    const gigDateTime = new Date(`${gigDate}T${timeStr}`);
    if (isNaN(gigDateTime.getTime())) return null;
    const activateAt = new Date(gigDateTime.getTime() - autoSettings.auto_activate_mins_before * 60000);
    const deactivateAt = new Date(gigDateTime.getTime() + autoSettings.auto_deactivate_mins_after * 60000);
    const now = new Date();
    return { activateAt, deactivateAt, inWindow: now >= activateAt && now <= deactivateAt };
  };

  const fd = (key, val) => setFormData(f => ({ ...f, [key]: val }));

  const upcomingGigs = gigs
    .filter(g => !isPastGig(g))
    .sort((a, b) => {
      if (!a.gig_date && !b.gig_date) return 0;
      if (!a.gig_date) return 1;
      if (!b.gig_date) return -1;
      return new Date(a.gig_date) - new Date(b.gig_date);
    });

  const pastGigs = gigs
    .filter(g => isPastGig(g))
    .sort((a, b) => {
      if (!a.gig_date && !b.gig_date) return 0;
      if (!a.gig_date) return 1;
      if (!b.gig_date) return -1;
      return new Date(b.gig_date) - new Date(a.gig_date);
    });

  const inputStyle = {
    width: '100%', padding: '0.75rem',
    border: `2px solid ${theme.border}`,
    borderRadius: '8px', fontSize: '1rem',
    boxSizing: 'border-box',
    background: theme.surface, color: theme.text,
  };

  const labelStyle = {
    display: 'block', marginBottom: '0.4rem',
    fontWeight: '600', color: theme.text, fontSize: '0.9rem',
  };

  const renderGigCard = (gig, readOnly = false) => {
    const isExpanded = expandedGigId === gig.id;
    const autoInfo = readOnly ? null : getAutoActivateInfo(gig);
    const now = new Date();

    return (
      <div
        key={gig.id}
        style={{
          padding: '1rem',
          background: readOnly ? theme.surfaceAlt : theme.surface,
          borderRadius: '12px',
          border: gig.is_active ? `2px solid ${theme.primary}` : `0.5px solid ${theme.border}`,
          boxShadow: theme.isDark ? 'none' : '0 2px 8px rgba(0,0,0,0.06)',
        }}
      >
        <div onClick={() => setExpandedGigId(isExpanded ? null : gig.id)} style={{ cursor: 'pointer' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem', flexWrap: 'wrap' }}>
                <h3 style={{ margin: 0, fontSize: '1.1rem', color: readOnly ? '#6c757d' : 'inherit' }}>{gig.gig_name}</h3>
                {gig.is_active && (
                  <span style={{ background: theme.primary, color: theme.onPrimary, padding: '0.2rem 0.6rem', borderRadius: '12px', fontSize: '0.7rem', fontWeight: '600' }}>
                    ACTIVE
                  </span>
                )}
                {readOnly && (
                  <span style={{ background: theme.surfaceAlt, color: theme.textMuted, padding: '0.2rem 0.6rem', borderRadius: '12px', fontSize: '0.7rem', fontWeight: '600', border: `1px solid ${theme.border}` }}>
                    <Lock size={11} style={{ marginRight: '0.2rem', verticalAlign: 'middle' }} />READ ONLY
                  </span>
                )}
                {autoInfo && autoInfo.inWindow && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.2rem', background: theme.isDark ? '#0a2318' : '#d4edda', color: theme.isDark ? '#2ecc71' : '#155724', padding: '0.2rem 0.5rem', borderRadius: '10px', fontSize: '0.7rem', fontWeight: '600' }}>
                    <Zap size={10} />Auto-active
                  </span>
                )}
                {autoInfo && !autoInfo.inWindow && autoInfo.activateAt > now && (
                  <span style={{ background: theme.surfaceAlt, color: theme.textMuted, padding: '0.2rem 0.5rem', borderRadius: '10px', fontSize: '0.7rem', border: `1px solid ${theme.border}` }}>
                    Auto {autoInfo.activateAt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                )}
              </div>
              <p style={{ margin: '0.25rem 0', color: theme.textSecondary, fontSize: '0.9rem' }}>
                <MapPin size={13} style={{ marginRight: '0.25rem', verticalAlign: 'middle' }} />
                {gig.venue_name}
                {gig.venue_location && ` · ${gig.venue_location}`}
                {gig.venue_postcode && ` · ${gig.venue_postcode}`}
              </p>
              {gig.gig_date && (
                <p style={{ margin: '0.25rem 0', color: theme.textSecondary, fontSize: '0.85rem' }}>
                  <Calendar size={13} style={{ marginRight: '0.25rem', verticalAlign: 'middle' }} />
                  {new Date(gig.gig_date).toLocaleDateString('en-GB', { weekday: 'short', month: 'short', day: 'numeric' })}
                  {gig.gig_time && ` at ${String(gig.gig_time).substring(0, 5)}`}
                </p>
              )}
            </div>
            <div style={{ fontSize: '1.5rem', color: theme.primary, transition: 'transform 0.2s', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</div>
          </div>
        </div>

        {isExpanded && (
          <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: `0.5px solid ${theme.border}` }}>
            {(gig.number_of_sets || gig.set_duration_minutes) && (
              <p style={{ margin: '0 0 0.5rem 0', color: theme.textSecondary, fontSize: '0.9rem' }}>
                <Music size={13} style={{ marginRight: '0.25rem', verticalAlign: 'middle' }} />
                {gig.number_of_sets} set{gig.number_of_sets > 1 ? 's' : ''}
                {gig.set_duration_minutes && ` · ${gig.set_duration_minutes} mins each`}
              </p>
            )}
            {gig.notes && (
              <p style={{ margin: '0.5rem 0', color: theme.textSecondary, fontSize: '0.9rem', fontStyle: 'italic' }}>{gig.notes}</p>
            )}
            {(gig.stage_info || gig.power_info) && (
              <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: theme.surfaceAlt, borderRadius: '6px' }}>
                {gig.stage_info && <p style={{ margin: '0 0 0.25rem 0', fontSize: '0.85rem', color: theme.textSecondary }}>Stage: {gig.stage_info}</p>}
                {gig.power_info && <p style={{ margin: 0, fontSize: '0.85rem', color: theme.textSecondary }}>Power: {gig.power_info}</p>}
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: isNarrow ? '1fr' : 'repeat(auto-fit, minmax(120px, 1fr))', gap: '0.5rem', marginTop: '1rem' }}>
              <button
                onClick={e => { e.stopPropagation(); window.location.href = `/dashboard/gigs/${gig.id}/setlist`; }}
                style={{ padding: '0.75rem 1rem', background: theme.primary, color: theme.onPrimary, border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: '500' }}
              >
                <FileText size={14} style={{ marginRight: '0.3rem', verticalAlign: 'middle' }} />Set List
              </button>
              <button
                onClick={e => { e.stopPropagation(); window.open(`/print/gig/${gig.unique_url}`, '_blank', 'noopener,noreferrer'); }}
                style={{ padding: '0.75rem 1rem', background: 'transparent', color: theme.text, border: `1px solid ${theme.border}`, borderRadius: '6px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: '500' }}
              >
                <Printer size={14} style={{ marginRight: '0.3rem', verticalAlign: 'middle' }} />Promo Sheet
              </button>
              {gig.venue_postcode && (
                <button
                  onClick={e => openDirections(e, gig)}
                  style={{ padding: '0.75rem 1rem', background: 'transparent', color: theme.text, border: `1px solid ${theme.border}`, borderRadius: '6px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: '500' }}
                >
                  <Navigation size={14} style={{ marginRight: '0.3rem', verticalAlign: 'middle' }} />Directions
                </button>
              )}
              {!readOnly && !gig.is_active && (
                <button
                  onClick={e => { e.stopPropagation(); handleActivate(gig.id, gig.gig_name); }}
                  style={{ padding: '0.75rem 1rem', background: theme.primary, color: theme.onPrimary, border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: '500' }}
                >
                  Set Active
                </button>
              )}
              {!readOnly && (
                <button
                  onClick={e => { e.stopPropagation(); openEditWizard(gig); }}
                  style={{ padding: '0.75rem 1rem', background: 'transparent', color: theme.text, border: `1px solid ${theme.border}`, borderRadius: '6px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: '500' }}
                >
                  Edit
                </button>
              )}
              {!readOnly && (
                <button
                  onClick={e => { e.stopPropagation(); handleDelete(gig.id, gig.gig_name); }}
                  style={{ padding: '0.75rem 1rem', background: 'transparent', color: '#dc3545', border: '1px solid rgba(220,53,69,0.4)', borderRadius: '6px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: '500' }}
                >
                  Delete
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) return <div style={{ padding: '2rem' }}>Loading gigs...</div>;

  return (
    <div style={{ background: theme.background }}>
      <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '2rem', color: theme.text }}>

        <button
          onClick={openAddWizard}
          style={{ padding: '0.75rem 1.5rem', background: theme.primary, color: theme.onPrimary, border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', marginBottom: '2rem' }}
        >
          + Add Gig
        </button>

        {upcomingGigs.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center', background: theme.surfaceAlt, borderRadius: '12px', color: theme.textSecondary }}>
            <p>No upcoming gigs. Create your first gig to get started!</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '1rem' }}>
            {upcomingGigs.map(gig => renderGigCard(gig, false))}
          </div>
        )}

        {pastGigs.length > 0 && (
          <div style={{ marginTop: '2rem' }}>
            <button
              onClick={() => setShowPastGigs(!showPastGigs)}
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'none', border: 'none', cursor: 'pointer', color: '#6c757d', fontSize: '0.95rem', fontWeight: '600', padding: '0.5rem 0', marginBottom: '1rem' }}
            >
              <span style={{ display: 'inline-block', transition: 'transform 0.2s', transform: showPastGigs ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
              Past Gigs ({pastGigs.length})
            </button>
            {showPastGigs && (
              <div style={{ display: 'grid', gap: '1rem' }}>
                {pastGigs.map(gig => renderGigCard(gig, true))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add/Edit Wizard Modal */}
      {wizardOpen && (
        <>
        <style>{`
          .rdp-input { width: 100%; padding: 0.75rem; border: 2px solid ${theme.border}; border-radius: 8px; font-size: 1rem; box-sizing: border-box; background: ${theme.surface}; color: ${theme.text}; cursor: pointer; }
          .rdp-input:focus { outline: none; border-color: ${theme.primary}; }
          .react-datepicker-popper { z-index: 9999 !important; }
          .react-datepicker { background: ${theme.surface} !important; color: ${theme.text} !important; border: 1px solid ${theme.border} !important; border-radius: 10px !important; font-family: system-ui, -apple-system, sans-serif !important; box-shadow: 0 8px 32px rgba(0,0,0,0.4) !important; }
          .react-datepicker__header { background: ${theme.surfaceAlt} !important; border-bottom: 1px solid ${theme.border} !important; border-top-left-radius: 10px !important; border-top-right-radius: 10px !important; padding-top: 10px !important; }
          .react-datepicker__current-month { color: ${theme.text} !important; font-weight: 700 !important; }
          .react-datepicker__day-name { color: ${theme.textMuted} !important; }
          .react-datepicker__day { color: ${theme.text} !important; border-radius: 6px !important; }
          .react-datepicker__day:hover { background: ${theme.primary}55 !important; color: ${theme.text} !important; }
          .react-datepicker__day--selected, .react-datepicker__day--keyboard-selected { background: ${theme.primary} !important; color: ${theme.onPrimary} !important; }
          .react-datepicker__day--today:not(.react-datepicker__day--selected) { border: 1.5px solid ${theme.primary} !important; font-weight: 700 !important; }
          .react-datepicker__day--outside-month { color: ${theme.textMuted} !important; opacity: 0.4; }
          .react-datepicker__navigation-icon::before { border-color: ${theme.text} !important; }
          .react-datepicker__navigation:hover .react-datepicker__navigation-icon::before { border-color: ${theme.primary} !important; }
          .react-datepicker__today-button { background: ${theme.surfaceAlt} !important; color: ${theme.primary} !important; border-top: 1px solid ${theme.border} !important; font-weight: 600 !important; }
          .react-datepicker__triangle { display: none !important; }
          .react-datepicker__close-icon::after { background-color: ${theme.primary} !important; }
        `}</style>
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
          onClick={e => { if (e.target === e.currentTarget) closeWizard(); }}
        >
          <div style={{ background: theme.surface, borderRadius: '16px', width: '100%', maxWidth: '480px', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', border: `1px solid ${theme.border}`, boxShadow: theme.isDark ? '0 20px 60px rgba(0,0,0,0.8)' : '0 20px 60px rgba(0,0,0,0.2)' }}>

            {/* Header */}
            <div style={{ padding: '1.25rem 1.5rem', borderBottom: `1px solid ${theme.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.1rem', color: theme.text }}>
                  {wizardMode === 'add' ? 'New Gig' : 'Edit Gig'}
                </h3>
                <p style={{ margin: '0.2rem 0 0', fontSize: '0.8rem', color: theme.textMuted }}>
                  {['The Gig', 'The Venue', 'Details'][wizardStep - 1]}
                </p>
              </div>
              <button onClick={closeWizard} style={{ background: 'none', border: 'none', fontSize: '1.6rem', cursor: 'pointer', color: theme.textMuted, lineHeight: 1, padding: 0 }}>×</button>
            </div>

            {/* Progress dots */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: '0.6rem', padding: '0.85rem 1.5rem 0' }}>
              {[1, 2, 3].map(s => (
                <div
                  key={s}
                  onClick={() => wizardMode === 'edit' && setWizardStep(s)}
                  style={{
                    width: 8, height: 8, borderRadius: '50%',
                    background: s <= wizardStep ? theme.primary : theme.border,
                    opacity: s < wizardStep ? 0.55 : 1,
                    cursor: wizardMode === 'edit' ? 'pointer' : 'default',
                    transition: 'all 0.2s',
                    transform: s === wizardStep ? 'scale(1.4)' : 'scale(1)',
                  }}
                />
              ))}
            </div>

            {/* Step content */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '1rem 1.5rem 0.5rem' }}>

              {wizardStep === 1 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div>
                    <label style={labelStyle}>Gig Name <span style={{ color: '#dc3545' }}>*</span></label>
                    <input
                      type="text"
                      value={formData.gig_name}
                      onChange={e => fd('gig_name', e.target.value)}
                      autoFocus
                      placeholder="e.g. Friday Night at The Crown"
                      style={inputStyle}
                    />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                      <label style={labelStyle}>Date</label>
                      <DatePicker
                        selected={formData.gig_date ? new Date(formData.gig_date + 'T12:00:00') : null}
                        onChange={date => {
                          if (!date) { fd('gig_date', ''); return; }
                          const y = date.getFullYear();
                          const m = String(date.getMonth() + 1).padStart(2, '0');
                          const d = String(date.getDate()).padStart(2, '0');
                          fd('gig_date', `${y}-${m}-${d}`);
                        }}
                        dateFormat="dd/MM/yyyy"
                        placeholderText="Select date"
                        todayButton="Today"
                        isClearable
                        popperProps={{ strategy: 'fixed' }}
                        customInput={<input className="rdp-input" readOnly placeholder="Select date" />}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>Start Time</label>
                      <div style={{ display: 'flex', gap: '0.3rem', alignItems: 'center' }}>
                        <select
                          value={(formData.gig_time || '').split(':')[0] || ''}
                          onChange={e => {
                            const mins = (formData.gig_time || ':00').split(':')[1] || '00';
                            fd('gig_time', e.target.value ? `${e.target.value}:${mins}` : '');
                          }}
                          style={{ ...inputStyle, flex: 1, cursor: 'pointer', paddingLeft: '0.5rem', paddingRight: '0.25rem' }}
                        >
                          <option value="">--</option>
                          {Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0')).map(h => (
                            <option key={h} value={h}>{h}</option>
                          ))}
                        </select>
                        <span style={{ color: theme.textMuted, fontWeight: '700', fontSize: '1.1rem', flexShrink: 0 }}>:</span>
                        <select
                          value={(formData.gig_time || '').split(':')[1] || '00'}
                          onChange={e => {
                            const hrs = (formData.gig_time || '00:').split(':')[0] || '00';
                            fd('gig_time', `${hrs}:${e.target.value}`);
                          }}
                          style={{ ...inputStyle, flex: 1, cursor: 'pointer', paddingLeft: '0.5rem', paddingRight: '0.25rem' }}
                        >
                          {Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, '0')).map(m => (
                            <option key={m} value={m}>{m}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {wizardStep === 2 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div>
                    <label style={labelStyle}>Venue Name <span style={{ color: '#dc3545' }}>*</span></label>
                    <input
                      type="text"
                      value={formData.venue_name}
                      onChange={e => fd('venue_name', e.target.value)}
                      autoFocus
                      placeholder="e.g. The Crown & Anchor"
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Location</label>
                    <input
                      type="text"
                      value={formData.venue_location}
                      onChange={e => fd('venue_location', e.target.value)}
                      placeholder="e.g. Manchester, UK"
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Postcode</label>
                    <input
                      type="text"
                      value={formData.venue_postcode}
                      onChange={e => fd('venue_postcode', e.target.value.toUpperCase())}
                      placeholder="e.g. M1 1AE"
                      maxLength={10}
                      style={{ ...inputStyle, textTransform: 'uppercase' }}
                    />
                  </div>
                </div>
              )}

              {wizardStep === 3 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                      <label style={labelStyle}>Number of Sets</label>
                      <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', border: `1px solid ${theme.border}` }}>
                        {[1, 2, 3].map(n => (
                          <button
                            key={n}
                            type="button"
                            onClick={() => fd('number_of_sets', n)}
                            style={{
                              flex: 1, padding: '0.65rem 0', fontSize: '0.95rem', fontWeight: 700,
                              border: 'none', borderRight: n < 3 ? `1px solid ${theme.border}` : 'none',
                              cursor: 'pointer', fontFamily: 'inherit',
                              background: formData.number_of_sets === n ? theme.primary : theme.surfaceAlt,
                              color: formData.number_of_sets === n ? theme.onPrimary : theme.textSecondary,
                            }}
                          >{n}</button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label style={labelStyle}>Set Duration (mins)</label>
                      <input
                        type="number"
                        min="1"
                        value={formData.set_duration_minutes}
                        onChange={e => fd('set_duration_minutes', e.target.value)}
                        style={inputStyle}
                      />
                    </div>
                  </div>
                  <div>
                    <label style={labelStyle}>Notes</label>
                    <textarea
                      value={formData.notes}
                      onChange={e => fd('notes', e.target.value)}
                      placeholder="General notes about this gig..."
                      rows={3}
                      style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Stage Info</label>
                    <input
                      type="text"
                      value={formData.stage_info}
                      onChange={e => fd('stage_info', e.target.value)}
                      placeholder="e.g. Small stage, 2m x 3m"
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Power Info</label>
                    <input
                      type="text"
                      value={formData.power_info}
                      onChange={e => fd('power_info', e.target.value)}
                      placeholder="e.g. 2x 13A sockets available"
                      style={inputStyle}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div style={{ padding: '1rem 1.5rem', borderTop: `1px solid ${theme.border}`, display: 'flex', gap: '0.75rem', justifyContent: 'space-between', alignItems: 'center' }}>
              <button
                onClick={closeWizard}
                style={{ padding: '0.7rem 1.1rem', background: 'none', color: theme.textSecondary, border: `1px solid ${theme.border}`, borderRadius: '8px', cursor: 'pointer', fontWeight: '500' }}
              >
                Cancel
              </button>
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                {wizardStep > 1 && (
                  <button
                    onClick={() => setWizardStep(s => s - 1)}
                    style={{ padding: '0.7rem 1.25rem', background: theme.surfaceAlt, color: theme.text, border: `1px solid ${theme.border}`, borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}
                  >
                    Back
                  </button>
                )}
                {wizardStep < 3 ? (
                  <button
                    onClick={() => {
                      if (wizardStep === 1 && !formData.gig_name.trim()) { toast.error('Gig name is required'); return; }
                      if (wizardStep === 2 && !formData.venue_name.trim()) { toast.error('Venue name is required'); return; }
                      setWizardStep(s => s + 1);
                    }}
                    style={{ padding: '0.7rem 1.5rem', background: theme.primary, color: theme.onPrimary, border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}
                  >
                    Next
                  </button>
                ) : (
                  <button
                    onClick={handleSubmit}
                    disabled={submitting}
                    style={{ padding: '0.7rem 1.5rem', background: submitting ? '#aaa' : theme.primary, color: theme.onPrimary, border: 'none', borderRadius: '8px', cursor: submitting ? 'not-allowed' : 'pointer', fontWeight: '600' }}
                  >
                    {submitting ? 'Saving…' : wizardMode === 'add' ? 'Create Gig' : 'Update Gig'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </>
      )}
    </div>
  );
}

export default GigManagement;
